import { layerPropertiesAtom } from '@/atoms';
import {
  deleteElevationProfileAtom,
  ElevationProfile,
  elevationProfilesAtom,
  elevationProfileToolModeAtom,
  renameElevationProfileAtom,
  toggleElevationProfileAtom,
} from '@/atoms/elevationProfile.atom';
import { crestSourcesAtom } from '@/atoms/crestLine.atom';
import { fragmentTreeOpenAtom } from '@/atoms/fragmentTree.atom';
import {
  defaultLayerSettings,
  isLayerSelectedAtom,
  LayerData,
  layerRuntimeCentersAtom,
  layersAtom,
  layerSettingsAtom,
  layerSettingsStorageKey,
  selectedLayerIdsAtom,
  selectedLayersAtom,
  setLayersAtom,
  toggleLayerSelectionAtom,
} from '@/atoms/layers.atom';
import {
  clearMeasurementsAtom,
  deleteMeasurementAtom,
  measurementsAtom,
  renameMeasurementAtom,
  StoredMeasurement,
  toggleComponentsAtom,
  toggleMeasurementAtom,
} from '@/atoms/measurements.atom';
import { stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { pointCloudTargetsAtom, viewerEvents } from '@/atoms/viewer.atom';
import { CrestLineDialog } from '@/components/CrestLineDialog';
import { DeviationRenderGallery } from '@/components/DeviationRenderGallery';
import LandxmlExportDialog, { LANDXML_EXPORT_DIALOG_CLASS } from '@/components/LandxmlExportDialog';
import { LayerSettingsDialog } from '@/components/LayerSettingsDialog';
import PortalTooltip from '@/components/PortalTooltip';
import { isClearanceCandidate, RunClearanceDialog } from '@/components/RunClearanceDialog';
import { isDeviationReferenceCandidate, RunDeviationAnalysisDialog } from '@/components/RunDeviationAnalysisDialog';
import { RunGroundClassificationDialog } from '@/components/RunGroundClassificationDialog';
import { RunMeshExportDialog } from '@/components/RunMeshExportDialog';
import { RunRainfallAnalysisDialog } from '@/components/RunRainfallAnalysisDialog';
import { RunLayerStackDialog } from '@/components/RunLayerStackDialog';
import { isSurfaceCandidate, RunSurfaceDifferenceDialog } from '@/components/RunSurfaceDifferenceDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { execute, getLinkSessionId } from '@/graphql/execute';
import { Layer, LayerType } from '@/graphql/graphql';
import { useExclusiveCreationTool, type CreationTool } from '@/hooks/useCreationTool';
import { useEphemeralLayerHeartbeat } from '@/hooks/useEphemeralLayerHeartbeat';
import { useFeature, useFeatureCheck } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { QUICK_UPLOAD_ACCEPT_ATTR, useQuickUpload } from '@/hooks/useQuickUpload';
import { useSavedViewMutations, useSavedViews, type SavedViewListItem } from '@/hooks/useSavedViews';
import { useViewCaptureApply } from '@/hooks/useViewCaptureApply';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { downloadFile as sharedDownloadFile } from '@/lib/download';
import { profileToCsv, profileToGeoJson } from '@/lib/elevationProfile/profileExport';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { isStationVolumesByproduct } from '@/lib/stationVolumes/byproducts';
import { convexHullXY, extractOuterBoundary } from '@/lib/stockpile/pointInPolygon';
import { cn } from '@/lib/utils';
import { useAlert } from '@/providers/AlertProvider';
import { useDialog } from '@/providers/DialogProvider';
import { layerByproductsQuery } from '@/queries/byproduct.query';
import {
  deleteLayerMutation,
  layerSettingsQuery,
  layersQuery,
  refreshWmsLayerMutation,
  updateLayerMutation,
} from '@/queries/layer.query';
import { meQuery } from '@/queries/me.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  BoundingBoxSolid,
  BuildingSolid,
  ChartLineSolid,
  CogOne,
  Download,
  Edit,
  EyeSlash,
  FolderSolid,
  ImageSolid,
  LayersThreeSolid,
  LockSolid,
  MapSolid,
  Path,
  Plus,
  RadioSolid,
  RefreshAlt,
  RulerSolid,
  Search,
  Square,
  Trash,
  TreeSolid,
  Triangle,
} from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Bookmark, ChevronDown, ChevronRight, Crosshair, Link, Loader2, Users } from 'lucide-react';
import { FunctionComponent, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LayerEditorContent } from '../ProjectLayers';
import { ANALYSIS_DIALOG_CLASS, ANALYSIS_DIALOG_OPTIONS } from '@/lib/analysisDialog';

interface iLayersProps {
  projectId: string;
  selectedLayerId?: string;
  onSelectLayer?: (layerId: string) => void;
}

interface LayerItemProps {
  layer: LayerData & { subLayers: LayerData[] };
  onToggleLayer: (layerId: string) => void;
  onTurnOffLayer: (layerId: string) => void;
  isLayerSelected: (layerId: string) => boolean;
  isLayerIndeterminate: (layerId: string) => boolean;
  expandedLayers: Set<string>;
  onToggleExpanded: (layerId: string) => void;
  is3DDataAvailable: boolean;
  selectedLayerId?: string;
  onSelectLayer?: (layerId: string) => void;
  depth?: number;
}

export const LayerIcon = ({ type }: { type: LayerData['type'] }) => {
  switch (type) {
    case 'VECTOR':
      return <BoundingBoxSolid className='flex-shrink-0 size-4 text-fuchsia-700 dark:text-pink-500' />;
    case 'IMAGE':
      return <ImageSolid className='flex-shrink-0 size-4 text-sky-700 dark:text-sky-500' />;
    case 'POINTCLOUD':
      return <RadioSolid className='flex-shrink-0 size-4 stroke-2 text-green-500 dark:text-lime-500' />;
    case 'GROUP':
      return <FolderSolid className='flex-shrink-0 size-4 stroke-2 text-gray-700 dark:text-gray-300' />;
    case 'MODEL':
      return <BuildingSolid className='flex-shrink-0 size-4 text-orange-600 dark:text-amber-500' />;
    case 'TILE':
      return <MapSolid className='flex-shrink-0 size-4 text-orange-600 dark:text-amber-500' />;
    case 'WMS':
      return <MapSolid className='flex-shrink-0 size-4 text-green-600 dark:text-green-500' />;
  }
  return null;
};

/**
 * Per-layer "create geometry" popover. Replaces the previous pair of inline
 * buttons (Add Point + Add Surface) — adding line and polygon entries would
 * have made the layer row visibly crowded on narrow viewports. The trigger
 * shows an active-tool tint when this layer is the current target so the
 * user can see at a glance which row is being edited.
 *
 * Tool entries are mutually exclusive: clicking the active tool turns it off,
 * clicking any other tool switches to it (and resets the previous one).
 */
const CreateGeometryPopover = ({
  activeTool,
  onTogglePoint,
  onToggleLine,
  onTogglePolygon,
  onToggleSurface,
}: {
  activeTool: CreationTool | null;
  onTogglePoint: () => void;
  onToggleLine: () => void;
  onTogglePolygon: () => void;
  onToggleSurface: () => void;
}) => {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);

  const lineGate = useFeature('viewer.addLine');
  const polygonGate = useFeature('viewer.addPolygon');

  const triggerColor = activeTool
    ? activeTool === 'point'
      ? 'bg-orange-500 text-white hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700'
      : activeTool === 'line'
        ? 'bg-emerald-500 text-white hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700'
        : activeTool === 'polygon'
          ? 'bg-purple-500 text-white hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700'
          : 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
    : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800/50';

  const wrap = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
    setOpen(false);
  };

  const wrapGated = (gateAllowed: boolean, promptUpgrade: () => void, handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!gateAllowed) {
      promptUpgrade();
    } else {
      handler();
    }
    setOpen(false);
  };

  const itemClass = (active: boolean, color: string, locked = false) =>
    cn(
      'flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-left transition-colors',
      active
        ? `${color} text-white`
        : 'text-slate-700 hover:bg-slate-100 dark:text-stone-100 dark:hover:bg-stone-800/50',
      locked && !active && 'opacity-50',
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-tooltip-id='layers-create-geometry'
          className={cn('size-6 rounded-md flex items-center justify-center transition-colors', triggerColor)}
          title={t('viewerLayers.createGeometry')}>
          <Plus className='size-4 stroke-2' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-48 p-1' align='end' sideOffset={4}>
        <button
          type='button'
          onClick={wrap(onTogglePoint)}
          className={itemClass(activeTool === 'point', 'bg-orange-500 hover:bg-orange-600')}>
          <Plus className='size-4 stroke-2' />
          {t('viewerLayers.addPointsToLayer')}
        </button>
        <button
          type='button'
          onClick={wrapGated(lineGate.allowed, lineGate.promptUpgrade, onToggleLine)}
          className={itemClass(activeTool === 'line', 'bg-emerald-500 hover:bg-emerald-600', !lineGate.allowed)}>
          <Path className='size-4 stroke-2' />
          {t('viewerLayers.addLinesToLayer')}
        </button>
        <button
          type='button'
          onClick={wrapGated(polygonGate.allowed, polygonGate.promptUpgrade, onTogglePolygon)}
          className={itemClass(activeTool === 'polygon', 'bg-purple-500 hover:bg-purple-600', !polygonGate.allowed)}>
          <Triangle className='size-4 stroke-2' />
          {t('viewerLayers.addPolygonsToLayer')}
        </button>
        <button
          type='button'
          onClick={wrap(onToggleSurface)}
          className={itemClass(activeTool === 'surface', 'bg-blue-500 hover:bg-blue-600')}>
          <Square className='size-4 stroke-2' />
          {t('viewerLayers.addSurfacesToLayer')}
        </button>
      </PopoverContent>
    </Popover>
  );
};

const LayerItem: FunctionComponent<LayerItemProps> = ({
  layer,
  onToggleLayer,
  isLayerSelected,
  isLayerIndeterminate,
  is3DDataAvailable,
  expandedLayers,
  onToggleExpanded,
  onTurnOffLayer,
  selectedLayerId,
  onSelectLayer,
  depth = 0,
}) => {
  const { t } = useTranslation('project');
  const { hasRole } = useOrganizations();
  const { projectId } = useParams();
  const dialog = useDialog();
  const alert = useAlert();
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  // Stored centre first; otherwise whatever the renderer derived from the
  // loaded geometry (LandXML layers without a persisted `center`).
  const runtimeCenters = useAtomValue(layerRuntimeCentersAtom);
  const jumpCenter: [number, number, number] | undefined =
    (layer.center?.coords as [number, number, number] | undefined) ?? runtimeCenters[layer.id];
  const setLayerProperties = useSetAtom(layerPropertiesAtom);
  const setLayerSettings = useSetAtom(layerSettingsAtom(layer.id));
  const setFragmentTreeOpen = useSetAtom(fragmentTreeOpenAtom);
  const client = useQueryClient();
  // useExclusiveCreationTool subscribes to all four creation-mode atoms via
  // useAtom internally, so this row re-renders whenever any tool flips for
  // any layer. No need for separate useAtomValue reads here.
  const {
    activeToolForLayer,
    isPointActive,
    isLineActive,
    isPolygonActive,
    isSurfaceActive,
    togglePoint,
    toggleLine,
    togglePolygon,
    toggleSurface,
  } = useExclusiveCreationTool(layer.id);
  const allLayers = useAtomValue(layersAtom);
  // Registry of layers a crest line can actually be extracted from (TIN +
  // identity transform, see crestLine.atom.ts) — the menu item only shows
  // for a layer id present here.
  const crestSources = useAtomValue(crestSourcesAtom);
  // PostHog feature-flag gates for the analysis context-menu actions. Coerce
  // `undefined` (flag not yet loaded / no PostHog config) to `false` so the
  // menu items stay hidden until the flag is positively on — matches the
  // existing `!!isGoogleStreetViewEnabled` pattern in Viewer.tsx.
  const isDeviationAnalysisEnabled = !!useFeatureFlagEnabled('deviationAnalysis');
  const isGroundAnalysisEnabled = !!useFeatureFlagEnabled('groundAnalysis');
  const isStockpileAnalysisEnabled = !!useFeatureFlagEnabled('stockpileAnalysis');
  const isRainfallAnalysisEnabled = !!useFeatureFlagEnabled('rainfallAnalysis');
  const isMeshExportEnabled = !!useFeatureFlagEnabled('meshExport');
  const isSurfaceDifferenceEnabled = !!useFeatureFlagEnabled('surfaceDifference');
  const isLayerStackEnabled = !!useFeatureFlagEnabled('stack');
  const isCrestEnabled = !!useFeatureFlagEnabled('crest');
  // Subscription-tier gates layered on top of the PostHog flags. The menu
  // items stay visible (so users learn the feature exists) but clicking
  // when locked routes to the upgrade dialog instead of opening the analysis
  // dialog. Imperative `useFeatureCheck` so we can branch inside handlers.
  const featureCheck = useFeatureCheck();
  // CrestLineDialog needs the project's SRID to tag/reproject the geometry
  // it saves. Same queryKey as useLayers' project fetch above, so this dedupes
  // against the cache rather than issuing a second request per row.
  const { data: crestProjectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId! }),
    enabled: !!projectId,
  });
  const setStockpileToolMode = useSetAtom(stockpileToolModeAtom);
  // Use the live PCO atom rather than filtering layers by `isHidden` —
  // `pointCloudTargetsAtom` is populated by `<PointCloud>` only with PCOs
  // that are *actually loaded into the scene*. The layer's `isHidden` field
  // is server-side soft-delete state, NOT the user-toggled eye icon: a
  // child PCO turned off via the eye toggle stays `isHidden=false` on the
  // record (it's removed from `selectedLayerIds` instead). Filtering by
  // `isHidden` therefore wrongly reported a hidden child as still visible.
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);

  const refreshWmsMutation = useMutation({
    mutationFn: async (layerId: string) => {
      await execute(refreshWmsLayerMutation, { layerId });
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['layers'] });
      toast.success(t('viewerLayers.wmsLayersRefreshed'));
    },
    onError: () => {
      toast.error(t('viewerLayers.failedRefreshWmsLayers'));
    },
  });
  const [isLocalSettings, setIsLocalSettings] = useState(false);

  const hasSubLayers = layer.subLayers.length > 0;
  const isExpanded = expandedLayers.has(layer.id);
  const indentClass = depth > 0 ? `ml-${depth * 4}` : '';
  const isIndeterminate = isLayerIndeterminate(layer.id);
  const isSelected = isLayerSelected(layer.id);
  const isHiddenByParent = useMemo(() => {
    const findParentHidden = (parentId: string | null | undefined): boolean => {
      if (!parentId) return false;
      const parent = allLayers.find((candidate) => candidate.id === parentId);
      if (!parent) return false;
      return parent.isHidden || findParentHidden(parent.parentLayerId);
    };
    return findParentHidden(layer.parentLayerId);
  }, [allLayers, layer.parentLayerId]);
  const isServerHidden = layer.isHidden || isHiddenByParent;
  // While an ephemeral layer is still being processed (no root yet), disable
  // toggle + per-layer actions — there's no data to show, configure, or measure.
  // Delete stays enabled so the user can cancel an upload.
  const isProcessing =
    !!(layer as any).isEphemeral && ((layer as any).metadata?.quickUploadStatus === 'PROCESSING' || !layer.root);

  const _handleOpenLayerSettings = () => {
    dialog.openDialog(<LayerSettingsDialog layer={layer} isEditor={hasRole('EDITOR')} />);
  };

  const _handleUseDefaultSettings = async () => {
    const data = await execute(layerSettingsQuery, {
      id: layer.id,
    });
    setLayerSettings(data.layer?.settings || layer.settings || defaultLayerSettings);
    setTimeout(() => {
      localStorage.removeItem(layerSettingsStorageKey(layer.id));
    }, 100);
  };

  const _handleOpenLayerProperties = () => {
    setLayerProperties(layer);
  };

  const _handleOpenLayerEditor = () => {
    dialog.openDialog(
      <LayerEditorContent
        projectId={projectId!}
        layer={layer}
        onSuccess={() => {
          client.invalidateQueries({ queryKey: ['layers', projectId] });
          client.invalidateQueries({ queryKey: ['layersNested', projectId] });
        }}
      />,
    );
  };

  const _handleDeleteLayer = (layerId: string) => {
    alert.openAlert({
      title: t('layers.deleteLayerTitle'),
      content: t('layers.deleteLayerDescription'),
      actions: [
        {
          label: t('layers.deleteLayer'),
          variant: 'destructive',
          onClick: () => _handleExecuteDeleteLayer(layerId),
        },
      ],
    });
  };

  const _handleToggleLock = async () => {
    const toastId = toast.loading(t('layers.updatingLayer'));
    try {
      await execute(updateLayerMutation, { input: { id: layer.id, isLocked: !layer.isLocked } });
      toast.success(t('layers.layerUpdated'), { id: toastId });
      client.invalidateQueries({ queryKey: ['layers', projectId] });
      client.invalidateQueries({ queryKey: ['layersNested', projectId] });
    } catch (error) {
      toast.error(t('layers.failedUpdateLayer'), { id: toastId });
    }
  };

  const _handleToggleHidden = async () => {
    const toastId = toast.loading(t('layers.updatingLayer'));
    try {
      if (!layer.isHidden) onTurnOffLayer(layer.id);
      await execute(updateLayerMutation, { input: { id: layer.id, isHidden: !layer.isHidden } });
      toast.success(t('layers.layerUpdated'), { id: toastId });
      client.invalidateQueries({ queryKey: ['layers', projectId] });
      client.invalidateQueries({ queryKey: ['layersNested', projectId] });
    } catch (error) {
      toast.error(t('layers.failedUpdateLayer'), { id: toastId });
    }
  };

  const _handleExecuteDeleteLayer = async (layerId: string) => {
    const toastId = toast.loading(t('layers.deletingLayer'));
    try {
      await execute(deleteLayerMutation, { id: layerId });
      toast.success(t('layers.layerDeleted'), { id: toastId });
      client.invalidateQueries({ queryKey: ['layers', projectId] });
      client.invalidateQueries({ queryKey: ['layersNested', projectId] });
      onTurnOffLayer(layerId);
    } catch (error) {
      toast.error(t('layers.failedDeleteLayer'), { id: toastId });
    }
  };

  // Aliases used by row-styling/badges below — keep names stable to minimise
  // diff churn from the previous (point + surface only) layout.
  const isAddPointActive = isPointActive;
  const isAddSurfaceActive = isSurfaceActive;
  const isAddLineActive = isLineActive;
  const isAddPolygonActive = isPolygonActive;

  const _handleRunDeviationAnalysis = () => {
    const gate = featureCheck('analysis.deviation');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    const candidates = allLayers.filter((l) => isDeviationReferenceCandidate(l) && l.id !== layer.id);
    dialog.openDialog(
      <RunDeviationAnalysisDialog sourceLayer={layer} candidateReferenceLayers={candidates} projectId={projectId!} />,
      undefined,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  const _handleRunClearance = () => {
    const gate = featureCheck('analysis.clearance');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(
      <RunClearanceDialog
        projectId={projectId!}
        sourceLayer={layer}
        candidateLayers={allLayers.filter(isClearanceCandidate)}
      />,
      ANALYSIS_DIALOG_CLASS,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  const _handleRunSurfaceDifference = () => {
    const gate = featureCheck('analysis.surfaceDifference');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(
      <RunSurfaceDifferenceDialog
        projectId={projectId!}
        candidateLayers={allLayers.filter(isSurfaceCandidate)}
        initialTopLayerId={layer.id}
      />,
      undefined,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  /**
   * Multi-layer thickness (LAYER_STACK). A stack is built from the same
   * comparable surfaces a surface difference is, so it is offered wherever
   * that is — the difference is that a stack takes N of them rather than two,
   * and the dialog seeds its first level from the layer clicked.
   *
   * Gated on two independent axes: the PostHog `stack` flag controls whether
   * the entry appears at all (flag off === invisible, not a paywall), and the
   * subscription gate is layered on top of that — the item stays clickable on
   * every tier once visible, and opens the upgrade dialog, because the HTML
   * `disabled` attribute would swallow that click.
   */
  const _handleRunLayerStack = () => {
    const gate = featureCheck('analysis.layerStack');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    const candidates = allLayers.filter(isSurfaceCandidate);
    dialog.openDialog(
      <RunLayerStackDialog
        projectId={projectId!}
        // The clicked layer first: it is almost always the top of the stack.
        candidateLayers={[layer, ...candidates.filter((l) => l.id !== layer.id)].filter(isSurfaceCandidate)}
      />,
      ANALYSIS_DIALOG_CLASS,
      ANALYSIS_DIALOG_OPTIONS,
    );
  };

  const _handleExtractCrestLine = () => {
    const gate = featureCheck('analysis.crestLine');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    const srid = crestProjectData?.project?.srid;
    if (srid == null) return;
    dialog.openDialog(
      <CrestLineDialog sourceLayerId={layer.id} projectId={projectId!} srid={srid} />,
      // House standard for the run-analysis dialogs.
      'flex max-h-[80vh] min-w-0 flex-col overflow-hidden',
      // The ONE non-modal dialog in the app, and the only one that needs to
      // be. Its "Draw centreline" mode picks points off the 3D canvas and its
      // live crest preview is drawn in the scene behind it — a modal dialog
      // puts `pointer-events: none` on <body> and a 50%-black overlay over the
      // viewport, so every canvas click hit the overlay and dismissed the
      // dialog instead of adding a vertex. See `DialogOptions.modal`.
      { modal: false },
    );
  };

  const _handleExportLandxml = () => {
    const gate = featureCheck('export.landxml');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(
      <LandxmlExportDialog projectId={projectId!} layerId={layer.id} layerName={layer.name} />,
      LANDXML_EXPORT_DIALOG_CLASS,
    );
  };

  const _handleRunGroundClassification = () => {
    const gate = featureCheck('analysis.groundClassify');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(<RunGroundClassificationDialog sourceLayer={layer} projectId={projectId!} />);
  };

  const _handleRunMeshExport = () => {
    const gate = featureCheck('analysis.meshExport');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    dialog.openDialog(<RunMeshExportDialog sourceLayer={layer} projectId={projectId!} />);
  };

  /**
   * "Run rainfall analysis" entry on a VECTOR layer. The user is expected
   * to have triangulated terrain (TIN) features in the picked layer — the
   * worker validates triangle shape on read and fails fast with a clear
   * error if not, so we don't pre-filter the menu by feature shape.
   */
  const _handleRunRainfallAnalysis = () => {
    dialog.openDialog(<RunRainfallAnalysisDialog sourceLayer={layer} projectId={projectId!} />);
  };

  /**
   * "Calculate stockpile volume" entry on a VECTOR layer. Useful when the
   * user already has the pile outline as one or more polygon features —
   * including TINs exported as a fan of triangle polygons. We collect every
   * polygon vertex in the layer, take the 2-D convex hull as the outer
   * boundary, auto-pick the only visible point cloud as the sampling source,
   * and seed the stockpile tool atom — the compute panel opens immediately.
   *
   * Convex-hull caveat: TINs are usually convex from above (or close to it),
   * so the hull matches the real footprint. Genuinely concave pile outlines
   * would over-include the dent — v2 could replace this with a polygon-union
   * via turf if it becomes a real complaint.
   */
  const _handleCalculateStockpileForLayer = async () => {
    const gate = featureCheck('viewer.stockpileFromSurface');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    if (layer.type !== 'VECTOR') return;
    const featuresData = await loadFeaturesByLayerOfflineAware([layer.id], projectId!);
    const polygonFeatures = featuresData.filter((f) => {
      const geom = f.geometry as { type?: string } | null;
      return geom?.type === 'Polygon' || geom?.type === 'MultiPolygon';
    });
    if (polygonFeatures.length === 0) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Collect every polygon's outer ring as a separate ring entry. The
    // boundary extractor needs them as ring lists, not a flat vertex pile —
    // edge topology matters. Inner rings (holes) are discarded; they don't
    // belong to the outer boundary.
    const allRings: Array<Array<[number, number, number]>> = [];
    const allVerts: Array<[number, number, number]> = [];
    for (const f of polygonFeatures) {
      const geom = f.geometry as
        | { type: 'Polygon'; coordinates: number[][][] }
        | { type: 'MultiPolygon'; coordinates: number[][][][] }
        | null;
      if (!geom) continue;
      const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;
      for (const poly of polys) {
        const outerRing = poly?.[0];
        if (!outerRing) continue;
        const ring: Array<[number, number, number]> = [];
        for (const v of outerRing) {
          const tup: [number, number, number] = [v[0], v[1], v[2] ?? 0];
          ring.push(tup);
          allVerts.push(tup);
        }
        allRings.push(ring);
      }
    }
    if (allRings.length === 0) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Edge-based boundary extraction. For TINs (many triangle polygons sharing
    // edges), this produces the *exact* concave outer boundary along the
    // triangulation perimeter — no over-inclusion. For single polygons or
    // disjoint multi-polygons, see the docs on `extractOuterBoundary`.
    let polygonRing = extractOuterBoundary(allRings);
    if (!polygonRing || polygonRing.length < 3) {
      // Fall back to convex hull only when topology fails (no closed boundary
      // ring could be built). Better an over-inclusive hull than an error.
      polygonRing = convexHullXY(allVerts);
    }
    if (polygonRing.length < 3) {
      toast.error(t('viewerLayers.stockpileLayerNoPolygons'));
      return;
    }

    // Auto-pick visible PCO from the live atom — only PCOs actually loaded
    // into the scene appear here, so a hidden child or an unloaded layer
    // is correctly excluded.
    const visiblePcoLayerIds = pcoTargets
      .map((p) => p.userData?.layerId as string | undefined)
      .filter((id): id is string => !!id);
    if (visiblePcoLayerIds.length === 0) {
      toast.error(t('viewerLayers.stockpileNoVisiblePco'));
      return;
    }
    const firstPcoLayer = allLayers.find((l) => l.id === visiblePcoLayerIds[0]);
    if (visiblePcoLayerIds.length > 1) {
      toast.warning(
        t('viewerLayers.stockpileMultiplePcosWarn', {
          name: firstPcoLayer?.name ?? visiblePcoLayerIds[0],
        }),
      );
    }
    setStockpileToolMode({
      enabled: true,
      phase: 'closed',
      pickedPoints: polygonRing,
      sourceLayerId: visiblePcoLayerIds[0],
      editFeatureId: null,
    });
  };

  const byproductsQuery = useQuery({
    queryKey: ['layerByproducts', layer.id],
    queryFn: () => execute(layerByproductsQuery, { layerId: layer.id }),
    enabled: !!layer.id,
    staleTime: 60_000,
  });

  // The server already withholds internal data files (`isInternal`) and
  // scopes the list to this layer's own run; the filename rule stays only for
  // rows written before the flag existed.
  const byproducts = (byproductsQuery.data?.layerByproducts ?? []).filter(
    (b) => !isStationVolumesByproduct(b.filename),
  );

  const _handleDownloadByproduct = (byproduct: { downloadUrl: string; filename: string }) => {
    const a = document.createElement('a');
    a.href = byproduct.downloadUrl;
    a.download = byproduct.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(t('viewerLayers.byproductDownloadStarted', { filename: byproduct.filename }));
  };

  // The deviation worker emits all per-surface renders bundled in a single
  // ZIP byproduct named `renders-<lazBasename>.zip`. The gallery dialog
  // unzips it in-browser via JSZip to display thumbnails + lightbox without
  // creating one byproduct row per render.
  const rendersZip = byproducts.find((b) => b.type === 'ZIP' && b.filename.toLowerCase().startsWith('renders-'));

  const _handleViewRenders = () => {
    if (!rendersZip) return;
    dialog.openDialog(
      <DeviationRenderGallery
        layerName={layer.name}
        zipUrl={rendersZip.downloadUrl}
        zipFilename={rendersZip.filename}
      />,
      'sm:max-w-[1100px]',
    );
  };

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (open) {
          setIsLocalSettings(!!window.localStorage.getItem(layerSettingsStorageKey(layer.id)));
        }
      }}>
      <ContextMenuTrigger>
        <div className={cn('select-none', depth > 0 && 'border-l border-gray-200 dark:border-stone-800 pl-3')}>
          <div
            className={cn(
              'flex flex-row items-center gap-1 py-1 pr-1.5 rounded-md transition-colors',
              indentClass,
              isAddPointActive && 'bg-orange-50 dark:bg-orange-950/20',
              isAddSurfaceActive && 'bg-blue-50 dark:bg-blue-950/20',
              isAddLineActive && 'bg-emerald-50 dark:bg-emerald-950/20',
              isAddPolygonActive && 'bg-purple-50 dark:bg-purple-950/20',
              selectedLayerId === layer.id && 'bg-slate-100 dark:bg-stone-800',
              onSelectLayer && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-stone-800/50',
            )}
            onClick={() => onSelectLayer?.(layer.id)}>
            {hasSubLayers && (
              <button
                className='flex flex-shrink-0 items-center justify-center size-5 hover:bg-gray-100 dark:hover:bg-stone-800/50 rounded'
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpanded(layer.id);
                }}>
                <ChevronDown
                  className={cn(
                    'size-4 text-gray-600 stroke-2 stroke-current transition-all dark:text-white/70',
                    isExpanded ? 'rotate-0' : '-rotate-90',
                  )}
                />
              </button>
            )}

            <div className='flex items-center gap-1 mr-2 flex-1 overflow-hidden'>
              <div className='size-5 flex items-center justify-center'>
                {isProcessing ? (
                  <Loader2 className='size-3.5 animate-spin text-blue-500' />
                ) : (
                  <LayerIcon type={layer.type} />
                )}
              </div>

              <Label
                className={cn(
                  'text-sm font-[450] flex-1 cursor-pointer line-clamp-1 overflow-hidden text-ellipsis',
                  isProcessing && 'opacity-60',
                  isServerHidden && 'line-through text-slate-500 dark:text-stone-500',
                )}>
                {isServerHidden && (
                  <EyeSlash
                    data-tooltip-id={layer.isHidden ? 'layers-hidden-self' : 'layers-hidden'}
                    className='inline-block size-3.5 stroke-2 mr-1 align-[-2px]'
                  />
                )}
                {layer.metadata?.source && viewerSettings.showLayerSource === true && (
                  <span className='text-xs font-semibold text-slate-700 dark:text-gray-200 bg-slate-100 border border-slate-200/70 dark:bg-stone-800 dark:border-stone-700 font-mono px-1.5 rounded-full mr-1'>
                    {layer.metadata?.source}
                  </span>
                )}
                {layer.name}
                {isProcessing && (
                  <span className='text-[10px] font-medium text-blue-600 dark:text-blue-400 ml-1'>
                    · {t('quickUpload.processing')}
                  </span>
                )}
                {isAddPointActive && (
                  <span className='text-xs font-semibold text-orange-700 dark:text-orange-400 bg-orange-100 border border-orange-200/70 dark:bg-orange-900/30 dark:border-orange-800 px-1.5 rounded-full ml-1'>
                    {t('viewerLayers.addingPoints')}
                  </span>
                )}
                {isAddSurfaceActive && (
                  <span className='text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-100 border border-blue-200/70 dark:bg-blue-900/30 dark:border-blue-800 px-1.5 rounded-full ml-1'>
                    {t('viewerLayers.addingSurfaces')}
                  </span>
                )}
                {isAddLineActive && (
                  <span className='text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100 border border-emerald-200/70 dark:bg-emerald-900/30 dark:border-emerald-800 px-1.5 rounded-full ml-1'>
                    {t('viewerLayers.addingLines')}
                  </span>
                )}
                {isAddPolygonActive && (
                  <span className='text-xs font-semibold text-purple-700 dark:text-purple-400 bg-purple-100 border border-purple-200/70 dark:bg-purple-900/30 dark:border-purple-800 px-1.5 rounded-full ml-1'>
                    {t('viewerLayers.addingPolygons')}
                  </span>
                )}
              </Label>
            </div>
            {layer.type === 'VECTOR' && hasRole('EDITOR') && !layer.isLocked && is3DDataAvailable && !isProcessing && (
              <CreateGeometryPopover
                activeTool={activeToolForLayer}
                onTogglePoint={togglePoint}
                onToggleLine={toggleLine}
                onTogglePolygon={togglePolygon}
                onToggleSurface={toggleSurface}
              />
            )}
            {layer.isLocked && (
              <LockSolid
                data-tooltip-id='layers-locked'
                className='size-4 stroke-2 text-slate-700 dark:text-stone-200/70 mr-1'
              />
            )}

            <Checkbox
              id={layer.id}
              checked={isSelected || isIndeterminate}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={() => onToggleLayer(layer.id)}
              indeterminate={isIndeterminate}
              disabled={isProcessing || isServerHidden}
            />
          </div>

          {hasSubLayers && isExpanded && (
            <div className='pl-2.5 ml-px'>
              {layer.subLayers.map((subLayer) => (
                <LayerItem
                  key={subLayer.id}
                  layer={subLayer as LayerData & { subLayers: LayerData[] }}
                  is3DDataAvailable={is3DDataAvailable}
                  onToggleLayer={onToggleLayer}
                  onTurnOffLayer={onTurnOffLayer}
                  isLayerSelected={isLayerSelected}
                  isLayerIndeterminate={isLayerIndeterminate}
                  expandedLayers={expandedLayers}
                  onToggleExpanded={onToggleExpanded}
                  selectedLayerId={selectedLayerId}
                  onSelectLayer={onSelectLayer}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem disabled className='cursor-default opacity-100' style={{ opacity: 1 }}>
          <span className='flex items-center gap-1'>
            <LayerIcon type={layer.type} />
            {layer.name}
          </span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuCheckboxItem
          checked={isSelected}
          disabled={isProcessing || isServerHidden}
          onClick={() => !isProcessing && !isServerHidden && onToggleLayer(layer.id)}>
          {t('viewerLayers.showLayer')}
        </ContextMenuCheckboxItem>
        <ContextMenuItem disabled={layer.type === 'GROUP' || isProcessing} onClick={_handleOpenLayerProperties}>
          <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.layerProperties')}
        </ContextMenuItem>
        {jumpCenter && !isProcessing && (
          <ContextMenuItem
            onClick={() => {
              const [x, y, z] = jumpCenter;
              viewerEvents.emit('jumpToTarget', { x, y, z: z || 0 });
            }}>
            <Crosshair className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('viewerLayers.jumpToLayerCenter')}
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem disabled={layer.type === 'GROUP' || isProcessing} onClick={_handleOpenLayerSettings}>
          <CogOne className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.openDisplaySettings')}
        </ContextMenuItem>
        <ContextMenuItem onClick={_handleUseDefaultSettings} disabled={!isLocalSettings || isProcessing}>
          <RefreshAlt className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.resetDisplaySettings')}
        </ContextMenuItem>
        {layer.type === 'MODEL' && (layer.metadata as any)?.type === 'FRAGMENT' && (
          <ContextMenuItem disabled={isProcessing} onClick={() => setFragmentTreeOpen(true)}>
            <TreeSolid className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('viewerLayers.openFragmentTree')}
          </ContextMenuItem>
        )}
        {layer.type === 'MODEL' &&
          ['FRAGMENT', 'IFC'].includes((layer.metadata as any)?.type) &&
          hasRole('EDITOR') &&
          !getLinkSessionId() && (
            <ContextMenuItem disabled={isProcessing} onClick={_handleExportLandxml}>
              <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('landxmlExport.menuLayer')}
            </ContextMenuItem>
          )}
        {layer.type === 'POINTCLOUD' && hasRole('EDITOR') && !getLinkSessionId() && (
          // The clearance entry carries no PostHog flag (ruling R10), so this
          // block is never empty and the separator is always earned — the old
          // `(isDeviationAnalysisEnabled || …)` guard that existed to avoid a
          // stray separator would now always be true, and saying so plainly
          // beats leaving it true by accident.
          <>
            <ContextMenuSeparator />
            {isDeviationAnalysisEnabled && (
              <ContextMenuItem disabled={isProcessing} onClick={_handleRunDeviationAnalysis}>
                <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                {t('viewerLayers.runDeviationAnalysis')}
              </ContextMenuItem>
            )}
            {isGroundAnalysisEnabled && (
              <ContextMenuItem disabled={isProcessing} onClick={_handleRunGroundClassification}>
                <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                {t('viewerLayers.runGroundClassification')}
              </ContextMenuItem>
            )}
            {isMeshExportEnabled && (
              <ContextMenuItem disabled={isProcessing} onClick={_handleRunMeshExport}>
                <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                {t('meshExport.generateMesh')}
              </ContextMenuItem>
            )}
            {/* Gated by the subscription, not by a flag: the item stays
                clickable on every tier and opens the upgrade dialog, because
                the HTML `disabled` attribute would swallow that click. */}
            <ContextMenuItem disabled={isProcessing} onClick={_handleRunClearance}>
              <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewerLayers.runClearance')}
            </ContextMenuItem>
          </>
        )}
        {/* Surface difference works on any two comparable surfaces, so this
            entry lives OUTSIDE the POINTCLOUD-only block above — it shows for
            LandXML / DXF / IFC / VECTOR / POINTCLOUD layers alike. */}
        {isSurfaceCandidate(layer) && hasRole('EDITOR') && !getLinkSessionId() && isSurfaceDifferenceEnabled && (
          <ContextMenuItem disabled={isProcessing} onClick={_handleRunSurfaceDifference}>
            <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('viewerLayers.runSurfaceDifference')}
          </ContextMenuItem>
        )}
        {/* Multi-layer thickness: the same comparable surfaces, N of them
            instead of two. Gated by the `stack` PostHog flag — off means the
            item does not render at all — with the subscription gate layered
            on top in the handler once it's visible. */}
        {isSurfaceCandidate(layer) && hasRole('EDITOR') && !getLinkSessionId() && isLayerStackEnabled && (
          <ContextMenuItem disabled={isProcessing} onClick={_handleRunLayerStack}>
            <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('layerStack.dialog.menuItem')}
          </ContextMenuItem>
        )}
        {/* Only for layers registered in crestSourcesAtom — a layer with no
            TIN, or one with a non-identity transform, has nothing to extract
            from (the sampler reads raw file coordinates). Also gated by the
            `crest` PostHog flag: while it's off the item is hidden entirely
            (no menu entry, no upgrade prompt) regardless of subscription
            tier. Once the flag is on, gating drops to subscription alone —
            the item stays clickable on every tier and opens the upgrade
            dialog on click when locked, because the HTML `disabled`
            attribute would swallow that click. */}
        {isCrestEnabled && crestSources.has(layer.id) && hasRole('EDITOR') && !getLinkSessionId() && (
          <ContextMenuItem disabled={isProcessing} onClick={_handleExtractCrestLine}>
            <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('crestLine.menuItem')}
          </ContextMenuItem>
        )}
        {/* Stockpile compute on a whole VECTOR layer — the polygon footprint
            for the compute is derived from every polygon feature in the
            layer (single feature → use as-is, many features → convex hull
            outer boundary). Useful for TINs exported as triangle layers. */}
        {layer.type === 'VECTOR' &&
          hasRole('EDITOR') &&
          !getLinkSessionId() &&
          (isStockpileAnalysisEnabled || isRainfallAnalysisEnabled) && (
            <>
              <ContextMenuSeparator />
              {isStockpileAnalysisEnabled && (
                <ContextMenuItem disabled={isProcessing} onClick={_handleCalculateStockpileForLayer}>
                  <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                  {t('viewerLayers.calculateStockpileForLayer')}
                </ContextMenuItem>
              )}
              {isRainfallAnalysisEnabled && (
                <ContextMenuItem disabled={isProcessing} onClick={_handleRunRainfallAnalysis}>
                  <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                  {t('rainfallAnalysis.menuItem')}
                </ContextMenuItem>
              )}
            </>
          )}
        {layer.type === 'WMS' && !layer.parentLayerId && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => refreshWmsMutation.mutate(layer.id)}
              disabled={refreshWmsMutation.isPending}>
              <RefreshAlt className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewerLayers.refreshWmsLayers')}
            </ContextMenuItem>
          </>
        )}
        {byproducts.length > 0 && (
          <>
            <ContextMenuSeparator />
            {rendersZip && (
              <ContextMenuItem onClick={_handleViewRenders}>
                <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                {t('viewerLayers.viewRenders')}
              </ContextMenuItem>
            )}
            {/* One entry, however many files: a run can leave five or six
                downloads behind, and each one as a top-level item pushed the
                layer's own actions off the bottom of the menu. */}
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
                {t('viewerLayers.downloads', { count: byproducts.length })}
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className='max-w-96'>
                {byproducts.map((bp) => (
                  <ContextMenuItem key={bp.id} onClick={() => _handleDownloadByproduct(bp)}>
                    <span className='truncate'>
                      {t('viewerLayers.downloadByproduct', { type: bp.type, filename: bp.filename })}
                    </span>
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        )}
        {hasRole('EDITOR') && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={_handleOpenLayerEditor} disabled={isProcessing}>
              <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewerLayers.openLayerEditor')}
            </ContextMenuItem>
            <ContextMenuCheckboxItem checked={layer.isLocked} onClick={_handleToggleLock} disabled={isProcessing}>
              {t('layers.lockLayer')}
            </ContextMenuCheckboxItem>
            <ContextMenuCheckboxItem checked={layer.isHidden} onClick={_handleToggleHidden} disabled={isProcessing}>
              {t('layers.disableLayer')}
            </ContextMenuCheckboxItem>
            <ContextMenuItem onClick={() => _handleDeleteLayer(layer.id)}>
              <Trash className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('layers.deleteLayer')}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const useLayers = ({ projectId }: { projectId: string }) => {
  const [layerList] = useAtom(layersAtom);
  const [selectedLayers] = useAtom(selectedLayersAtom(projectId));
  const [selectedLayerIds, setSelectedLayerIds] = useAtom(selectedLayerIdsAtom(projectId));
  const [, toggleLayerSelection] = useAtom(toggleLayerSelectionAtom(projectId));
  const [, updateLayers] = useAtom(setLayersAtom);
  const [isLayerSelected] = useAtom(isLayerSelectedAtom(projectId));

  // Initial layer selection depends on the project's defaultThirdPersonView
  // flag, so fetch it here too (deduped with Viewer's identical query).
  const { data: projectData, isPending: isProjectPending } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    // While any ephemeral layer is still being processed (placeholder hasn't been
    // filled in yet), poll every 3s so the user sees the layer flip from
    // "Processing…" to ready without a manual refresh.
    refetchInterval: (query) => {
      const layers = (query.state.data as any)?.layers as Array<any> | undefined;
      const hasProcessing = layers?.some(
        (l) => l.isEphemeral && (l.metadata?.quickUploadStatus === 'PROCESSING' || !l.root),
      );
      return hasProcessing ? 3000 : false;
    },
  });

  // Update the atom when data changes
  useEffect(() => {
    const layers = data?.layers;
    if (!layers) return;

    updateLayers(layers);
  }, [data?.layers, updateLayers]);

  // Separate effect for initial selection - only runs once when layers are first loaded
  useEffect(() => {
    const layers = data?.layers;
    if (!layers || selectedLayerIds.length > 0) return;
    // Wait for the flag rather than selecting images and yanking them away.
    // A failed project query resolves to isPending === false, so this can't deadlock.
    if (isProjectPending) return;

    const startInThirdPerson = projectData?.project?.defaultThirdPersonView === true;
    const getLayerById = (layerId: string) => layers.find((layer) => layer.id === layerId);
    const hasImages = layers.some((layer) => layer.type === 'IMAGE' && !layer.isHidden);
    const selectableTypes: Set<string> = startInThirdPerson
      ? new Set(['VECTOR', 'POINTCLOUD'])
      : new Set(['VECTOR', 'IMAGE', ...(!hasImages ? ['POINTCLOUD'] : [])]);
    const isSelectableLayer = (layer: LayerData) => {
      if (layer.isHidden) return false;
      if (!selectableTypes.has(layer.type)) return false;
      if (!layer.parentLayerId) return true;
      const parentLayer = getLayerById(layer.parentLayerId);
      return parentLayer?.isHidden !== true;
    };

    setSelectedLayerIds(layers.filter(isSelectableLayer).map((layer) => layer.id));
  }, [data?.layers, setSelectedLayerIds, isProjectPending, projectData?.project?.defaultThirdPersonView]);

  // Auto-enable an ephemeral layer (and its sub-layers) the moment processing
  // finishes, so the uploader sees their drop in the viewer without any extra click.
  // We track the previous state in a ref to detect the PROCESSING → READY edge.
  const prevProcessingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const layers = data?.layers;
    if (!layers) return;

    const isReady = (l: any) => l.isEphemeral && l.root && l.metadata?.quickUploadStatus !== 'PROCESSING';
    const isProcessing = (l: any) => l.isEphemeral && (!l.root || l.metadata?.quickUploadStatus === 'PROCESSING');

    const justReady = layers.filter((l: any) => isReady(l) && prevProcessingRef.current.has(l.id));
    if (justReady.length > 0) {
      const idsToSelect = justReady.flatMap((l: any) => [
        l.id,
        ...layers.filter((c: any) => c.parentLayerId === l.id).map((c: any) => c.id),
      ]);
      setSelectedLayerIds((prev) => {
        const next = new Set(prev);
        idsToSelect.forEach((id) => next.add(id));
        return Array.from(next);
      });
    }

    prevProcessingRef.current = new Set(layers.filter(isProcessing).map((l: any) => l.id));
  }, [data?.layers, setSelectedLayerIds]);

  // Helper function to get all sublayer IDs recursively
  const getAllSubLayerIds = (layerId: string): string[] => {
    const subLayerIds: string[] = [];

    const findSubLayers = (parentId: string) => {
      const children = layerList.filter((layer) => layer.parentLayerId === parentId);
      children.forEach((child) => {
        subLayerIds.push(child.id);
        findSubLayers(child.id); // Recursively find grandchildren
      });
    };

    findSubLayers(layerId);
    return subLayerIds;
  };

  // Helper function to check if a layer should show indeterminate state (only for GROUP layers)
  const isLayerIndeterminate = (layerId: string): boolean => {
    const layer = layerList.find((l) => l.id === layerId);
    if (!layer || layer.type !== 'GROUP') return false;

    const allSubLayerIds = getAllSubLayerIds(layerId);

    // If no sublayers, not indeterminate
    if (allSubLayerIds.length === 0) return false;

    const selectedSubLayers = allSubLayerIds.filter((id) => isLayerSelected(id));

    // Indeterminate if some but not all sublayers are selected
    return selectedSubLayers.length > 0 && selectedSubLayers.length < allSubLayerIds.length;
  };

  // Wrapper to check if a layer is selected - for GROUP layers, derive from children
  const isLayerSelectedWithGroups = (layerId: string): boolean => {
    const layer = layerList.find((l) => l.id === layerId);
    if (!layer) return false;

    // For GROUP layers, check if ALL sublayers are selected
    if (layer.type === 'GROUP') {
      const allSubLayerIds = getAllSubLayerIds(layerId);
      if (allSubLayerIds.length === 0) return false;
      return allSubLayerIds.every((id) => isLayerSelected(id));
    }

    // For non-GROUP layers, use the original selection check
    return isLayerSelected(layerId);
  };

  const _handleToggleLayer = (layerId: string) => {
    const isCurrentlySelected = isLayerSelected(layerId);
    const allSubLayerIds = getAllSubLayerIds(layerId);

    if (isCurrentlySelected) {
      const layersToDeselect = [layerId, ...allSubLayerIds];
      layersToDeselect.forEach((id) => {
        if (isLayerSelected(id)) {
          toggleLayerSelection(id);
        }
      });
    } else {
      const layersToSelect = [layerId, ...allSubLayerIds];
      layersToSelect.forEach((id) => {
        if (!isLayerSelected(id)) {
          toggleLayerSelection(id);
        }
      });
    }
  };

  const _handleTurnOffLayer = (layerId: string) => {
    const allSubLayerIds = getAllSubLayerIds(layerId);
    const layersToDeselect = [layerId, ...allSubLayerIds];
    layersToDeselect.forEach((id) => {
      if (isLayerSelected(id)) {
        toggleLayerSelection(id);
      }
    });
  };

  const _handleToggleLayerType = (type: LayerType) => {
    const layersForType = layerList.filter((layer) => layer.type === type) as Layer[];

    // For GROUP layers, collect all children IDs since GROUP layers themselves
    // are containers and not directly in the selectedLayerIds list
    const effectiveIds = layersForType.flatMap((layer) =>
      layer.type === 'GROUP' ? getAllSubLayerIds(layer.id) : [layer.id],
    );
    const uniqueIds = [...new Set(effectiveIds)];

    if (uniqueIds.length === 0) return;

    const isAllSelected = uniqueIds.every((id) => isLayerSelected(id));

    if (isAllSelected) {
      uniqueIds.forEach((id) => {
        if (isLayerSelected(id)) toggleLayerSelection(id);
      });
    } else {
      uniqueIds.forEach((id) => {
        if (!isLayerSelected(id)) toggleLayerSelection(id);
      });
    }
  };

  const layers = useMemo(() => {
    const buildLayerTree = (parentId: string | null): (LayerData & { subLayers: LayerData[] })[] => {
      return layerList
        .filter((layer) => layer.parentLayerId === parentId)
        .map((layer) => ({
          ...layer,
          subLayers: buildLayerTree(layer.id),
        }));
    };

    return buildLayerTree(null);
  }, [layerList]);

  return {
    layers,
    refetch,
    isLoading,
    enabledLayers: selectedLayers,
    onToggleLayer: _handleToggleLayer,
    onToggleLayerType: _handleToggleLayerType,
    onTurnOffLayer: _handleTurnOffLayer,
    isLayerSelected: isLayerSelectedWithGroups,
    isLayerIndeterminate,
  };
};

const getMeasurementLabel = (m: StoredMeasurement): string => {
  if (m.type === 'distance' && m.points.length === 2) {
    const [a, b] = m.points;
    const dist = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2 + (b[2] - a[2]) ** 2);
    return `${dist.toFixed(2)} m`;
  }
  if (m.type === 'area' && m.points.length >= 3) {
    // 3D area via cross-product summation: 0.5 * ‖Σ(vᵢ × vᵢ₊₁)‖
    let sx = 0,
      sy = 0,
      sz = 0;
    for (let i = 0; i < m.points.length; i++) {
      const [ax, ay, az] = m.points[i];
      const [bx, by, bz] = m.points[(i + 1) % m.points.length];
      sx += ay * bz - az * by;
      sy += az * bx - ax * bz;
      sz += ax * by - ay * bx;
    }
    const area = Math.sqrt(sx * sx + sy * sy + sz * sz) * 0.5;
    return `${area.toFixed(2)} m²`;
  }
  return '';
};

// Single-source download helper lives in @/lib/download — kept as a local
// alias so the many existing call sites in this file don't need touching.
const downloadFile = sharedDownloadFile;

type GeoJSONFeature = {
  type: 'Feature';
  properties: Record<string, string>;
  geometry: { type: string; coordinates: any };
};

function measurementToGeoJSON(m: StoredMeasurement): { type: 'FeatureCollection'; features: GeoJSONFeature[] } {
  const name = m.name || getMeasurementLabel(m);
  const properties = { name, type: m.type, value: getMeasurementLabel(m) };
  if (m.type === 'distance' && m.points.length === 2) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties, geometry: { type: 'LineString', coordinates: m.points } }],
    };
  }
  const ring = [...m.points, m.points[0]];
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties, geometry: { type: 'Polygon', coordinates: [ring] } }],
  };
}

/** Build a CRS object for GeoJSON (OGC URN format, understood by QGIS / GDAL). */
function geojsonCrs(srid: number) {
  return { type: 'name' as const, properties: { name: `urn:ogc:def:crs:EPSG::${srid}` } };
}

function measurementToCSV(m: StoredMeasurement): string {
  const header = 'point,x,y,z';
  const rows = m.points.map((p, i) => `${i + 1},${p[0]},${p[1]},${p[2]}`);
  return [header, ...rows].join('\n');
}

const RenameMeasurementDialog = ({
  measurement,
  projectId,
  onClose,
}: {
  measurement: StoredMeasurement;
  projectId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('project');
  const renameMeasurement = useSetAtom(renameMeasurementAtom(projectId));
  const [value, setValue] = useState(measurement.name ?? getMeasurementLabel(measurement));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed) {
      renameMeasurement({ id: measurement.id, name: trimmed });
    }
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('viewerLayers.renameMeasurement')}</DialogTitle>
        <DialogDescription>{t('viewerLayers.renameMeasurementDescription')}</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} className='my-4' />
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('viewerLayers.cancel')}
          </Button>
          <Button type='submit' disabled={!value.trim()}>
            {t('viewerLayers.rename')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

const MeasurementItem = ({
  measurement,
  projectId,
  srid,
}: {
  measurement: StoredMeasurement;
  projectId: string;
  srid?: number;
}) => {
  const { t } = useTranslation('project');
  const toggleMeasurement = useSetAtom(toggleMeasurementAtom(projectId));
  const deleteMeasurement = useSetAtom(deleteMeasurementAtom(projectId));
  const toggleComponents = useSetAtom(toggleComponentsAtom(projectId));
  const layers = useAtomValue(layersAtom);
  const selectedLayerIds = useAtomValue(selectedLayerIdsAtom(projectId));
  const toggleLayerSelection = useSetAtom(toggleLayerSelectionAtom(projectId));
  const dialog = useDialog();

  const handleJumpToLocation = () => {
    // Disable any active image layers so they don't obscure the jump target
    layers
      .filter((l) => l.type === 'IMAGE' && selectedLayerIds.includes(l.id))
      .forEach((l) => toggleLayerSelection(l.id));

    const pts = measurement.points;
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const cz = pts.reduce((s, p) => s + p[2], 0) / pts.length;
    viewerEvents.emit('jumpToTarget', { x: cx, y: cy, z: cz });
  };

  const handleOpenRename = () => {
    dialog.openDialog(
      <RenameMeasurementDialog
        measurement={measurement}
        projectId={projectId}
        onClose={() => dialog.dismissActiveDialog()}
      />,
    );
  };

  const handleDownloadGeoJSON = () => {
    const geojson = measurementToGeoJSON(measurement);
    const withCrs = {
      ...geojson,
      ...(srid && srid !== -1 ? { crs: geojsonCrs(srid) } : {}),
    };
    const filename = `${measurement.name || 'measurement'}.geojson`;
    downloadFile(JSON.stringify(withCrs, null, 2), filename);
    toast.success(t('viewerLayers.downloadedAsGeojson'));
  };

  const handleDownloadCSV = () => {
    const csv = measurementToCSV(measurement);
    const filename = `${measurement.name || 'measurement'}.csv`;
    downloadFile(csv, filename, 'text/csv');
    toast.success(t('viewerLayers.downloadedAsCsv'));
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div className='flex items-center gap-1 py-1 pr-1.5 rounded-md'>
          <div className='flex items-center gap-1 mr-2 flex-1 overflow-hidden'>
            <div className='size-5 flex items-center justify-center'>
              {measurement.type === 'distance' ? (
                <RulerSolid className='flex-shrink-0 size-4 text-red-600 dark:text-red-400' />
              ) : (
                <BoundingBoxSolid className='flex-shrink-0 size-4 text-orange-600 dark:text-orange-400' />
              )}
            </div>
            <Label className='text-sm font-[450] flex-1 cursor-pointer line-clamp-1 overflow-hidden text-ellipsis'>
              {measurement.name ? (
                <>
                  {measurement.name}
                  <span className='text-xs text-slate-400 dark:text-stone-500 ml-1'>
                    {getMeasurementLabel(measurement)}
                  </span>
                </>
              ) : (
                getMeasurementLabel(measurement)
              )}
            </Label>
          </div>
          <Checkbox checked={measurement.enabled} onCheckedChange={() => toggleMeasurement(measurement.id)} />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={handleOpenRename}>
          <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.rename')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleJumpToLocation}>
          <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.jumpToLocation')}
        </ContextMenuItem>
        {measurement.type === 'distance' && (
          <ContextMenuCheckboxItem
            checked={!!measurement.showComponents}
            onClick={() => toggleComponents(measurement.id)}>
            {t('viewerLayers.showComponents')}
          </ContextMenuCheckboxItem>
        )}
        <ContextMenuItem onClick={handleDownloadGeoJSON}>
          <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.downloadAsGeojson')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleDownloadCSV}>
          <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.downloadAsCsv')}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => deleteMeasurement(measurement.id)}>
          <Trash className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.delete')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

function allMeasurementsToGeoJSON(measurements: StoredMeasurement[], srid?: number) {
  return {
    type: 'FeatureCollection' as const,
    ...(srid && srid !== -1 ? { crs: geojsonCrs(srid) } : {}),
    features: measurements.flatMap((m) => measurementToGeoJSON(m).features),
  };
}

function allMeasurementsToCSV(measurements: StoredMeasurement[]): string {
  const header = 'measurement,type,point,x,y,z';
  const rows = measurements.flatMap((m) => {
    const name = m.name || getMeasurementLabel(m);
    return m.points.map((p, i) => `"${name}",${m.type},${i + 1},${p[0]},${p[1]},${p[2]}`);
  });
  return [header, ...rows].join('\n');
}

const RenameProfileDialog = ({
  profile,
  projectId,
  onClose,
}: {
  profile: ElevationProfile;
  projectId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('project');
  const renameProfile = useSetAtom(renameElevationProfileAtom(projectId));
  const [value, setValue] = useState(profile.name);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed) renameProfile({ id: profile.id, name: trimmed });
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('elevationProfile.renameTitle')}</DialogTitle>
        <DialogDescription>{t('elevationProfile.renameDescription')}</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} className='my-4' />
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('viewerLayers.cancel')}
          </Button>
          <Button type='submit' disabled={!value.trim()}>
            {t('viewerLayers.rename')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

const ElevationProfileItem = ({
  profile,
  projectId,
  srid,
}: {
  profile: ElevationProfile;
  projectId: string;
  srid?: number;
}) => {
  const { t } = useTranslation('project');
  const toggleProfile = useSetAtom(toggleElevationProfileAtom(projectId));
  const deleteProfile = useSetAtom(deleteElevationProfileAtom(projectId));
  const setMode = useSetAtom(elevationProfileToolModeAtom);
  const allLayers = useAtomValue(layersAtom);
  const dialog = useDialog();
  const sourceLayerName = profile.sourceLayerId
    ? (allLayers.find((l) => l.id === profile.sourceLayerId)?.name ?? undefined)
    : undefined;

  const handleOpenInPanel = () => {
    setMode({ phase: 'panel', activeId: profile.id });
  };

  const handleJumpToLocation = () => {
    // Centroid of the polyline vertices — same approach measurements use.
    const pts = profile.polylinePoints;
    if (pts.length === 0) return;
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const cz = pts.reduce((s, p) => s + p[2], 0) / pts.length;
    viewerEvents.emit('jumpToTarget', { x: cx, y: cy, z: cz });
  };

  const handleOpenRename = () => {
    dialog.openDialog(
      <RenameProfileDialog profile={profile} projectId={projectId} onClose={() => dialog.dismissActiveDialog()} />,
    );
  };

  const handleDownloadCsv = () => {
    if (profile.samples.length === 0) {
      toast.warning(t('elevationProfile.warnNoSamplesToExport'));
      return;
    }
    downloadFile(
      profileToCsv(profile, { sourceLayerName, srid }),
      `${profile.name || 'elevation-profile'}.csv`,
      'text/csv',
    );
    toast.success(t('elevationProfile.csvExportSuccess'));
  };

  const handleDownloadGeojson = () => {
    if (profile.samples.length === 0) {
      // Polyline-only export still works without samples (the polyline
      // feature is unconditional), but the panel-level convention is to
      // gate exports on having samples — keep parity here.
      toast.warning(t('elevationProfile.warnNoSamplesToExport'));
      return;
    }
    const fc = profileToGeoJson(profile, { sourceLayerName, srid });
    downloadFile(JSON.stringify(fc, null, 2), `${profile.name || 'elevation-profile'}.geojson`, 'application/geo+json');
    toast.success(t('elevationProfile.geojsonExportSuccess'));
  };

  const label = profile.totalLength > 0 ? `${profile.totalLength.toFixed(1)} m` : t('elevationProfile.notComputed');

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div className='flex items-center gap-1 py-1 pr-1.5 rounded-md'>
          <div className='flex items-center gap-1 mr-2 flex-1 overflow-hidden'>
            <div className='size-5 flex items-center justify-center'>
              <ChartLineSolid className='flex-shrink-0 size-4 text-sky-600 dark:text-sky-400' />
            </div>
            <Label
              className='text-sm font-[450] flex-1 cursor-pointer line-clamp-1 overflow-hidden text-ellipsis'
              onClick={handleOpenInPanel}>
              {profile.name ? (
                <>
                  {profile.name}
                  <span className='text-xs text-slate-400 dark:text-stone-500 ml-1'>{label}</span>
                </>
              ) : (
                label
              )}
            </Label>
          </div>
          <Checkbox checked={profile.enabled} onCheckedChange={() => toggleProfile(profile.id)} />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={handleOpenInPanel}>
          <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('elevationProfile.openInPanel')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleJumpToLocation}>
          <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.jumpToLocation')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleOpenRename}>
          <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.rename')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleDownloadCsv}>
          <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('elevationProfile.downloadCsv')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleDownloadGeojson}>
          <Download className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('elevationProfile.downloadGeojson')}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => deleteProfile(profile.id)}>
          <Trash className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewerLayers.delete')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

const ElevationProfilesSection = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation('project');
  const profiles = useAtomValue(elevationProfilesAtom(projectId));
  // Same fetch pattern as `MeasurementsSection` — the SRID is written into
  // each exported GeoJSON's `crs` property so QGIS / GDAL can reproject.
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });
  const srid = projectData?.project?.srid;
  if (profiles.length === 0) return null;

  return (
    <Collapsible defaultOpen className='mt-4 group/profiles'>
      <div className='flex items-center justify-between mb-2'>
        <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
          <div className='flex items-center justify-center size-5'>
            <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 duration-200 group-data-[state=open]/profiles:rotate-90 transition-all' />
          </div>
          <span className='text-xs font-semibold text-slate-700 dark:text-white'>
            {t('elevationProfile.sectionTitle')}
          </span>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        {profiles.map((p) => (
          <ElevationProfileItem key={p.id} profile={p} projectId={projectId} srid={srid} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const MeasurementsSection = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation('project');
  const alert = useAlert();
  const measurements = useAtomValue(measurementsAtom(projectId));
  const clearMeasurements = useSetAtom(clearMeasurementsAtom(projectId));
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });
  const srid = projectData?.project?.srid;

  if (measurements.length === 0) return null;

  const handleDownloadAllGeoJSON = () => {
    const geojson = allMeasurementsToGeoJSON(measurements, srid);
    downloadFile(JSON.stringify(geojson, null, 2), 'measurements.geojson');
    toast.success(t('viewerLayers.downloadedAsGeojson'));
  };

  const handleDownloadAllCSV = () => {
    const csv = allMeasurementsToCSV(measurements);
    downloadFile(csv, 'measurements.csv', 'text/csv');
    toast.success(t('viewerLayers.downloadedAsCsv'));
  };

  const handleClearAll = () => {
    alert.openAlert({
      title: t('viewerLayers.clearAllMeasurementsTitle'),
      content: t('viewerLayers.clearAllMeasurementsDescription'),
      actions: [
        {
          label: t('viewerLayers.clearAllMeasurements'),
          variant: 'destructive',
          onClick: () => clearMeasurements(),
        },
      ],
    });
  };

  return (
    <Collapsible defaultOpen className='mt-4 group/measurements'>
      <div className='flex items-center justify-between mb-2'>
        <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
          <div className='flex items-center justify-center size-5'>
            <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 duration-200 group-data-[state=open]/measurements:rotate-90 transition-all' />
          </div>
          <span className='text-xs font-semibold text-slate-700 dark:text-white'>{t('viewerLayers.measurements')}</span>
        </CollapsibleTrigger>
        <div className='flex items-center gap-0.5'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                data-tooltip-id='measurements-download-all'
                className='p-0.5 rounded-sm hover:bg-slate-200 dark:hover:bg-stone-800 transition-colors'>
                <Download className='size-3.5 text-slate-500 dark:text-stone-400' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={handleDownloadAllGeoJSON}>
                {t('viewerLayers.downloadAllAsGeojson')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadAllCSV}>{t('viewerLayers.downloadAllAsCsv')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <PortalTooltip delayShow={120} id='measurements-download-all'>
            {t('viewerLayers.downloadAllMeasurements')}
          </PortalTooltip>
          <button
            type='button'
            onClick={handleClearAll}
            data-tooltip-id='measurements-clear-all'
            className='p-0.5 rounded-sm hover:bg-slate-200 dark:hover:bg-stone-800 transition-colors'>
            <Trash className='size-3.5 text-slate-500 dark:text-stone-400' />
          </button>
          <PortalTooltip delayShow={120} id='measurements-clear-all'>
            {t('viewerLayers.clearAllMeasurements')}
          </PortalTooltip>
        </div>
      </div>
      <CollapsibleContent>
        {measurements.map((m) => (
          <MeasurementItem key={m.id} measurement={m} projectId={projectId} srid={srid} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const CreateSavedViewDialog = ({ projectId, onClose }: { projectId: string; onClose: () => void }) => {
  const { t } = useTranslation('project');
  const { captureCurrentView } = useViewCaptureApply(projectId);
  const { create } = useSavedViewMutations(projectId);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const current = captureCurrentView();
    if (!current) {
      toast.error(t('viewer3d.savedViews.captureFailed'));
      onClose();
      return;
    }
    create.mutate(
      {
        projectId,
        name: trimmed,
        visibility: shared ? 'SHARED' : 'PRIVATE',
        camera: current.camera,
        clipBox: current.clipBox,
        visibleLayerIds: current.visibleLayerIds,
        baseMapId: current.baseMapId,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('viewer3d.savedViews.createTitle')}</DialogTitle>
        <DialogDescription>{t('viewer3d.savedViews.createDescription')}</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('viewer3d.savedViews.namePlaceholder')}
          className='mt-4'
        />
        <label className='mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-stone-200 cursor-pointer'>
          <Checkbox checked={shared} onCheckedChange={(v) => setShared(!!v)} />
          {t('viewer3d.savedViews.shareWithTeam')}
        </label>
        <DialogFooter className='mt-4'>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('viewerLayers.cancel')}
          </Button>
          <Button type='submit' disabled={!name.trim() || create.isPending}>
            {t('viewer3d.savedViews.save')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

const RenameSavedViewDialog = ({
  view,
  projectId,
  onClose,
}: {
  view: SavedViewListItem;
  projectId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('project');
  const { update } = useSavedViewMutations(projectId);
  const [value, setValue] = useState(view.name);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed && trimmed !== view.name) {
      update.mutate({ id: view.id, name: trimmed });
    }
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('viewer3d.savedViews.renameTitle')}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} className='my-4' />
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            {t('viewerLayers.cancel')}
          </Button>
          <Button type='submit' disabled={!value.trim()}>
            {t('viewer3d.savedViews.rename')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

const SavedViewItem = ({
  view,
  projectId,
  canEdit,
}: {
  view: SavedViewListItem;
  projectId: string;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('project');
  const { applyView, captureCurrentView } = useViewCaptureApply(projectId);
  const { update, remove } = useSavedViewMutations(projectId);
  const dialog = useDialog();
  const isShared = view.visibility === 'SHARED';

  const handleUpdateToCurrent = () => {
    const current = captureCurrentView();
    if (!current) {
      toast.error(t('viewer3d.savedViews.captureFailed'));
      return;
    }
    update.mutate({
      id: view.id,
      camera: current.camera,
      clipBox: current.clipBox,
      visibleLayerIds: current.visibleLayerIds,
      baseMapId: current.baseMapId,
    });
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/projects/${projectId}?view=${view.id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    toast.success(t('viewer3d.savedViews.linkCopied'));
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <button
          type='button'
          onClick={() => applyView(view)}
          className='w-full flex items-center gap-1 py-1 pr-1.5 rounded-md text-left hover:bg-slate-50 dark:hover:bg-stone-800/50'>
          <div className='size-5 flex items-center justify-center'>
            <Bookmark className='flex-shrink-0 size-4 text-slate-500 dark:text-stone-400' />
          </div>
          <Label className='text-sm font-[450] flex-1 cursor-pointer line-clamp-1 overflow-hidden text-ellipsis'>
            {view.name}
          </Label>
          <span className='text-[10px] uppercase rounded px-1 py-0.5 bg-slate-100 text-slate-500 dark:bg-stone-800 dark:text-stone-400'>
            {isShared ? t('viewer3d.savedViews.shared') : t('viewer3d.savedViews.private')}
          </span>
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => applyView(view)}>
          <Crosshair className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewer3d.savedViews.apply')}
        </ContextMenuItem>
        <ContextMenuItem onClick={handleCopyLink}>
          <Link className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          {t('viewer3d.savedViews.copyLink')}
        </ContextMenuItem>
        {canEdit && (
          <>
            <ContextMenuItem onClick={handleUpdateToCurrent}>
              <RefreshAlt className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewer3d.savedViews.updateToCurrent')}
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() =>
                dialog.openDialog(
                  <RenameSavedViewDialog
                    view={view}
                    projectId={projectId}
                    onClose={() => dialog.dismissActiveDialog()}
                  />,
                )
              }>
              <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewer3d.savedViews.rename')}
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() =>
                update.mutate({
                  id: view.id,
                  visibility: isShared ? 'PRIVATE' : 'SHARED',
                })
              }>
              {isShared ? (
                <LockSolid className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              ) : (
                <Users className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              )}
              {isShared ? t('viewer3d.savedViews.makePrivate') : t('viewer3d.savedViews.makeShared')}
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={() => remove.mutate(view.id)}>
              <Trash className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('viewer3d.savedViews.delete')}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

const SavedViewsSection = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation('project');
  const { hasRole } = useOrganizations();
  const dialog = useDialog();
  const { data, isLoading } = useSavedViews(projectId);
  const { data: meData } = useQuery({ queryKey: ['me'], queryFn: () => execute(meQuery) });

  // Anonymous share-link viewers can apply existing views, but not create or modify them.
  const isSharedLink = !!getLinkSessionId();
  const currentUserId = meData?.me?.id;
  const canEditShared = hasRole('EDITOR');
  const views = data?.savedViews ?? [];
  const canEdit = (view: SavedViewListItem) =>
    !isSharedLink && (view.createdById === currentUserId || (view.visibility === 'SHARED' && canEditShared));

  return (
    <Collapsible defaultOpen className='mt-4 group/views'>
      <div className='flex items-center justify-between mb-2'>
        <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
          <div className='flex items-center justify-center size-5'>
            <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 duration-200 group-data-[state=open]/views:rotate-90 transition-all' />
          </div>
          <span className='text-xs font-semibold text-slate-700 dark:text-white'>
            {t('viewer3d.savedViews.panelTitle')}
          </span>
        </CollapsibleTrigger>
        {!isSharedLink && (
          <>
            <button
              type='button'
              data-tooltip-id='saved-views-create'
              onClick={() =>
                dialog.openDialog(
                  <CreateSavedViewDialog projectId={projectId} onClose={() => dialog.dismissActiveDialog()} />,
                )
              }
              className='p-0.5 rounded-sm hover:bg-slate-200 dark:hover:bg-stone-800 transition-colors'>
              <Plus className='size-3.5 text-slate-500 dark:text-stone-400' />
            </button>
            <PortalTooltip delayShow={120} id='saved-views-create'>
              {t('viewer3d.savedViews.createTitle')}
            </PortalTooltip>
          </>
        )}
      </div>
      <CollapsibleContent>
        {isLoading && (
          <span className='text-xs text-slate-400 dark:text-stone-500 pl-6'>{t('viewer3d.savedViews.loading')}</span>
        )}
        {!isLoading && views.length === 0 && (
          <span className='text-xs text-slate-400 dark:text-stone-500 pl-6'>{t('viewer3d.savedViews.empty')}</span>
        )}
        {views.map((view) => (
          <SavedViewItem key={view.id} view={view} projectId={projectId} canEdit={canEdit(view)} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatRemaining = (expiresAt: string | Date | null): string => {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return '0m';
  const totalMin = Math.round(ms / 60_000);
  if (totalMin >= 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }
  return `${totalMin}m`;
};

const QuickUploadButton: FunctionComponent<{ projectId: string; variant?: 'icon' | 'block' }> = ({
  projectId,
  variant = 'icon',
}) => {
  const { t } = useTranslation('project');
  const { handleFiles, isAllowed, isPending, isSharedLink } = useQuickUpload(projectId);
  const inputRef = useRef<HTMLInputElement>(null);

  // Logged-in users have drag-and-drop only; the button is just a discoverability
  // aid for anonymous shared-link viewers who otherwise wouldn't know to drop.
  if (!isAllowed || !isSharedLink) return null;

  const open = () => inputRef.current?.click();

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        accept={QUICK_UPLOAD_ACCEPT_ATTR}
        multiple
        className='hidden'
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {variant === 'icon' ? (
        <button
          type='button'
          onClick={open}
          disabled={isPending}
          title={t('quickUpload.addButton')}
          className='p-1 rounded-sm hover:bg-slate-200 dark:hover:bg-stone-800 transition-colors disabled:opacity-50'>
          {isPending ? (
            <Loader2 className='size-3.5 animate-spin text-blue-500' />
          ) : (
            <Plus className='size-3.5 text-slate-600 dark:text-stone-300' />
          )}
        </button>
      ) : (
        <button
          type='button'
          onClick={open}
          disabled={isPending}
          className='flex items-center justify-center gap-1.5 mb-3 px-3 py-1.5 rounded-md border border-dashed border-slate-300 dark:border-stone-700 text-xs text-slate-600 dark:text-stone-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50'>
          {isPending ? <Loader2 className='size-3.5 animate-spin' /> : <Plus className='size-3.5 stroke-2' />}
          {t('quickUpload.addButton')}
        </button>
      )}
    </>
  );
};

const EphemeralLayersSection: FunctionComponent<{
  projectId: string;
  layers: Layer[];
  onTurnOffLayer: (layerId: string) => void;
}> = ({ projectId, layers, onTurnOffLayer }) => {
  const { t } = useTranslation('project');
  const client = useQueryClient();
  const alert = useAlert();
  // Re-render every minute so countdowns tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(h);
  }, []);

  // Top-level ephemeral layers only — sub-layers come along for the ride visually inside the parent.
  const ephemerals = useMemo(() => layers.filter((l) => (l as any).isEphemeral && !l.parentLayerId), [layers]);

  const ephemeralIds = useMemo(() => ephemerals.map((l) => l.id), [ephemerals]);
  useEphemeralLayerHeartbeat(ephemeralIds);

  if (ephemerals.length === 0) return null;

  const totalBytes = ephemerals.reduce((s, l) => s + Number((l as any).ephemeralSourceBytes || 0), 0);
  const earliest = ephemerals
    .map((l) => (l as any).ephemeralExpiresAt)
    .filter(Boolean)
    .sort()[0];

  const handleDelete = (layerId: string) => {
    alert.openAlert({
      title: t('quickUpload.deleteTitle'),
      content: t('quickUpload.deleteDescription'),
      actions: [
        {
          label: t('quickUpload.delete'),
          variant: 'destructive',
          onClick: async () => {
            const toastId = toast.loading(t('layers.deletingLayer'));
            try {
              await execute(deleteLayerMutation, { id: layerId });
              toast.success(t('quickUpload.deleted'), { id: toastId });
              onTurnOffLayer(layerId);
              client.invalidateQueries({ queryKey: ['layers', projectId] });
              client.invalidateQueries({ queryKey: ['layersNested', projectId] });
            } catch {
              toast.error(t('layers.failedDeleteLayer'), { id: toastId });
            }
          },
        },
      ],
    });
  };

  return (
    <Collapsible defaultOpen className='mb-4 group/temp'>
      <div className='flex items-center justify-between mb-2 pr-1.5'>
        <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
          <div className='flex items-center justify-center size-5'>
            <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 duration-200 group-data-[state=open]/temp:rotate-90 transition-all' />
          </div>
          <span className='text-xs font-semibold text-slate-700 dark:text-white'>
            {t('quickUpload.temporaryLayersTitle')}
          </span>
        </CollapsibleTrigger>
        <div className='flex items-center gap-1'>
          <span className='text-[10px] text-slate-500 dark:text-stone-400 whitespace-nowrap'>
            {t('quickUpload.headerSummary', {
              duration: formatRemaining(earliest),
              size: formatBytes(totalBytes),
            })}
          </span>
          <QuickUploadButton projectId={projectId} variant='icon' />
        </div>
      </div>
      <CollapsibleContent>
        {ephemerals.map((layer) => {
          const meta = (layer as any).metadata || {};
          const isProcessing = meta.quickUploadStatus === 'PROCESSING' || !layer.root;
          return (
            <div
              key={layer.id}
              className='flex items-center gap-1.5 py-1 pr-1.5 group/temp-row hover:bg-slate-50 dark:hover:bg-stone-800/50 rounded-md'>
              <div className='size-5 flex items-center justify-center'>
                {isProcessing ? (
                  <Loader2 className='size-3.5 animate-spin text-blue-500' />
                ) : (
                  <LayerIcon type={layer.type} />
                )}
              </div>
              <div className='flex-1 min-w-0'>
                <div className='text-sm font-[450] truncate text-slate-700 dark:text-stone-100'>{layer.name}</div>
                <div className='text-[10px] text-slate-500 dark:text-stone-400'>
                  {isProcessing ? (
                    <span className='text-blue-600 dark:text-blue-400'>{t('quickUpload.processing')}</span>
                  ) : (
                    <>
                      {formatBytes(Number((layer as any).ephemeralSourceBytes || 0))} ·{' '}
                      {t('quickUpload.expiresLine', {
                        duration: formatRemaining((layer as any).ephemeralExpiresAt),
                      })}
                    </>
                  )}
                </div>
              </div>
              <button
                type='button'
                onClick={() => handleDelete(layer.id)}
                className='opacity-0 group-hover/temp-row:opacity-100 transition-opacity p-1 rounded hover:bg-slate-200 dark:hover:bg-stone-700'
                title={t('quickUpload.delete')}>
                <Trash className='size-3.5 text-slate-600 dark:text-stone-300' />
              </button>
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

const Layers: FunctionComponent<iLayersProps> = ({ projectId, selectedLayerId, onSelectLayer }) => {
  const { t } = useTranslation('project');
  const { hasRole } = useOrganizations();
  const dialog = useDialog();
  const client = useQueryClient();
  const { layers, onToggleLayer, isLayerSelected, isLayerIndeterminate, onToggleLayerType, onTurnOffLayer } = useLayers(
    { projectId },
  );
  const [params] = useSearchParams();
  // const [searchTerm, setSearchTerm] = useState('');
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const isLayers = params.get('layers') === 'true';
  const viewerSettings = useAtomValue(userViewerSettingsAtom);

  const handleToggleExpanded = (layerId: string) => {
    setExpandedLayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }
      return newSet;
    });
  };

  const layerTypes = Array.from(new Set(layers?.map((layer) => layer.type))).sort();

  const has3DLayer = (layerList: Layer[]): boolean =>
    layerList.some(
      (l) => (l.type === 'POINTCLOUD' && isLayerSelected(l.id)) || (l.subLayers && has3DLayer(l.subLayers as Layer[])),
    );
  const is3DDataAvailable = has3DLayer(layers as Layer[]);

  const handleCreateLayer = () => {
    dialog.openDialog(
      <LayerEditorContent
        projectId={projectId}
        onSuccess={() => {
          client.invalidateQueries({ queryKey: ['layers', projectId] });
          client.invalidateQueries({ queryKey: ['layersNested', projectId] });
        }}
      />,
    );
  };

  const renderLayerContent = (layersToRender: Layer[]) => {
    return layersToRender.map((layer) => (
      <LayerItem
        is3DDataAvailable={is3DDataAvailable}
        key={layer.id}
        layer={layer}
        onToggleLayer={onToggleLayer}
        isLayerSelected={isLayerSelected}
        isLayerIndeterminate={isLayerIndeterminate}
        expandedLayers={expandedLayers}
        onToggleExpanded={handleToggleExpanded}
        onTurnOffLayer={onTurnOffLayer}
        selectedLayerId={selectedLayerId}
        onSelectLayer={onSelectLayer}
      />
    ));
  };
  return (
    <div
      style={{
        maxHeight: `calc(100vh - var(--spacing) * 15)`,
      }}
      className={cn(
        'w-72 overflow-hidden pt-3 text-left border-l border-gray-200 dark:border-stone-800 h-full flex flex-col',
        isLayers ? 'flex' : 'hidden',
      )}>
      <div id='onboarding-layers-panel' className='flex items-center justify-between gap-3 mb-4 px-3.5'>
        <div className='flex flex-col leading-tight'>
          <span className='font-medium text-slate-800 dark:text-white flex items-center gap-1.5'>
            <LayersThreeSolid className='size-4' />
            {t('viewerLayers.title')}
          </span>
          <span className='text-xs text-slate-500 dark:text-white/70 font-medium'>{t('viewerLayers.description')}</span>
        </div>
        {hasRole('EDITOR') && !getLinkSessionId() && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-7 shrink-0 rounded-md'
            onClick={handleCreateLayer}
            title={t('layers.createLayer')}>
            <Plus className='size-4 stroke-2' />
            <span className='sr-only'>{t('layers.createLayer')}</span>
          </Button>
        )}
      </div>

      <PortalTooltip delayShow={120} id='layers-type-vector'>
        {t('viewerLayers.typeVector')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-locked'>
        {t('viewerLayers.typeLocked')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-add-point'>
        {t('viewerLayers.typeAddPoint')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-add-surface'>
        {t('viewerLayers.typeAddSurface')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-image'>
        {t('viewerLayers.typeImage')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-pointcloud'>
        {t('viewerLayers.typePointcloud')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-model'>
        {t('viewerLayers.typeModel')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-tile'>
        {t('viewerLayers.typeTile')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-group'>
        {t('viewerLayers.typeGroup')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-hidden-self'>
        {t('layers.hiddenSelf')}
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-hidden'>
        {t('layers.hiddenParent')}
      </PortalTooltip>

      <div className='overflow-y-auto flex-1 flex flex-col px-3.5 pb-4'>
        {layers.some((l) => (l as any).isEphemeral) ? (
          <EphemeralLayersSection projectId={projectId} layers={layers as Layer[]} onTurnOffLayer={onTurnOffLayer} />
        ) : (
          <QuickUploadButton projectId={projectId} variant='block' />
        )}
        {viewerSettings.showLayerUnderType
          ? layerTypes.map((type) => {
              const layersForType = layers.filter((layer) => layer.type === type) as Layer[];
              const isAllLayersSelected = layersForType.every((layer) => isLayerSelected(layer.id));
              const isAnyLayerSelected = layersForType.some(
                (layer) => isLayerSelected(layer.id) || isLayerIndeterminate(layer.id),
              );
              return (
                <Collapsible key={type} defaultOpen className='mb-4 group/type'>
                  <div className='flex items-center justify-between mb-2 pr-1.5'>
                    <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
                      <div className='flex items-center justify-center size-5'>
                        <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 duration-200 group-data-[state=open]/type:rotate-90 transition-all' />
                      </div>
                      <span className='text-xs font-semibold text-slate-700 dark:text-white'>
                        {t(`viewerLayers.layerType${type.charAt(0) + type.slice(1).toLowerCase()}` as any)}
                      </span>
                    </CollapsibleTrigger>
                    <Checkbox
                      checked={isAnyLayerSelected}
                      indeterminate={!isAllLayersSelected}
                      onCheckedChange={() => {
                        onToggleLayerType(type);
                      }}
                    />
                  </div>
                  <CollapsibleContent>{renderLayerContent(layersForType)}</CollapsibleContent>
                </Collapsible>
              );
            })
          : renderLayerContent(layers as Layer[])}
        <MeasurementsSection projectId={projectId} />
        <ElevationProfilesSection projectId={projectId} />
        <SavedViewsSection projectId={projectId} />
      </div>
    </div>
  );
};

export default memo(Layers);
